import { ImmunizationRecord } from "../../models/Interfaces";
import db from "../../database/database";
import React, { useState, useEffect } from "react";
import { ErrorModal } from "../../components/modals/ErrorModal";
import { useTranslation } from "react-i18next";
import "../../components/patientCard.css";
import ReportCard from "../../components/ReportCard";
import { getReportForState } from "../../utils/reportUtils";
import { useLocation } from "react-router-dom";
import getIzProvider from "../../models/providers/IzProviders";
import { reportTypeFromString } from "../../apiClient";
import { DocketAPIError, getAPIClient } from "../../apiClient";
import { arrayBufferToBase64 } from "../../utils/reportUtils";
import { fetchRecordsUntilUpdated } from "../../utils/polling";

interface ReportListProps {
  record: ImmunizationRecord;
}

interface Report {
  contents: string;
  reportType: string;
  reportTitle: string;
  loading: boolean;
}

export default function ReportList() {
  const { state } = useLocation();
  const { record } = (state as ReportListProps) || {};
  const { t } = useTranslation();
  const [showErrorModal, setShowErrorModal] = useState<boolean | string>();
  const [reports, setReports] = useState<Map<number, Report>>(new Map());
  const providerKey = record?.izProviderKey;
  let izProvider;
  if (providerKey) {
    izProvider = getIzProvider(providerKey, t);
  }
  const reportTitles = izProvider?.reportTitles;

  const getReportContents = async (id: string): Promise<string> => {
    const reportResponse = await db().getItem(id);
    return typeof reportResponse === "string" && reportResponse.length > 0
      ? (reportResponse as string)
      : "";
  };

  const getReportId = (index: number) => {
    const reportType = getReportForState(providerKey, index);
    const convertedReportType = reportTypeFromString(reportType);
    return record.patientId + "_" + record.searchUid + "_" + convertedReportType;
  };

  const initializeReports = async () => {
    if (!reportTitles || !record) return;

    let newReports = new Map<number, Report>();
    await Promise.all(
      reportTitles.map(async (title, index) => {
        const contents = await getReportContents(getReportId(index));
        newReports.set(index, {
          contents,
          reportType: getReportForState(providerKey, index),
          reportTitle: title,
          loading: false,
        });
      })
    );

    setReports(newReports);
  };

  useEffect(() => {
    initializeReports();
  }, [reportTitles?.join(",")]);

  const noPatient = () => {
    return (
      <div className="mb-4" key="0">
        {t("immunizations.reports_no_patient")}
      </div>
    );
  };

  const getReport = async (index: number) => {
    const report = reports.get(index);
    if (!report) return;

    report.loading = true;
    setReports(new Map(reports.set(index, report)));

    try {
      const response = await getAPIClient().getRecordReport(
        record.searchUid,
        reportTypeFromString(report.reportType),
        record.izProviderKey
      );

      const buffer = arrayBufferToBase64(response);
      if (buffer.length > 0) {
        report.contents = "data:application/pdf;base64," + buffer;
        setReports(new Map(reports.set(index, report)));
        await db().setItem(getReportId(index), "data:application/pdf;base64," + buffer);
      }
    } catch (e: unknown) {
      if (e instanceof DocketAPIError) {
        setShowErrorModal(e.message);
      }
    } finally {
      report.loading = false;
      setReports(new Map(reports.set(index, report)));
    }
  };

  const hardRefresh = async () => {
    const currReports = Array.from(reports.values());
    currReports.forEach((report, index) => {
      if (report.contents.length === 0) return;

      report.loading = true;
      setReports(new Map(reports.set(index, report)));
    });
    await getAPIClient().refreshRecords();
    await fetchRecordsUntilUpdated();
    currReports.forEach(async (report, index) => {
      if (report.contents.length === 0) return;

      // getReport handles loading state
      await getReport(index);
    });
  };

  const reportCards = () => {
    return Array.from(reports.values()).map((report, index) => (
      <div className="" key={index}>
        <ReportCard
          reportContents={report.contents}
          reportTitle={report.reportTitle}
          onRefresh={hardRefresh}
          loading={report.loading}
          onDownload={() => getReport(index)}
        />
      </div>
    ));
  };

  return (
    <>
      {showErrorModal && (
        <ErrorModal onCloseModal={() => setShowErrorModal(false)}>{showErrorModal}</ErrorModal>
      )}
      <div className="flex flex-col docket-gap-1 max-md:px-6 max-md:pt-6 max-md:pb-20">
        {record ? reportCards() : noPatient()}
      </div>
    </>
  );
}
